import { ChevronDownIcon, ChevronLeftIcon, Share2Icon } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useGetChat, useGetDoclist } from "~/api/chat/chat";
import { useGetProject } from "~/api/projects/projects";
import { useGetCaseShareTokens } from "~/api/users/users";
import { Badge } from "~/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Skeleton } from "~/components/ui/skeleton";
import { useCasePresence } from "~/features/case/hooks/useCasePresence";
import { useParentCase } from "~/features/case/hooks/useParentCase";
import { FirstStepsChecklist } from "~/features/onboarding/components/FirstStepsChecklist";
import { useChatDocumentParams } from "~/helpers";
import { Button } from "../../../../components/ui/button";
import { CaseInfoPopover } from "../modals/CaseInfoPopover";
import { ShareCaseDialog } from "../modals/ShareCaseDialog";
import { PresenceAvatars } from "../presence/PresenceAvatars";
import { ChatTitle } from "./DocAreaChatTitle";

type DocAreaHeaderProps = {
  onNavigateBack: () => void;
};

export const DocAreaHeader: FC<DocAreaHeaderProps> = ({ onNavigateBack }) => {
  const navigate = useNavigate();
  const handleGoBackToCase = () => {
    onNavigateBack();
  };

  const [caseInfoPopoverOpen, setCaseInfoPopoverOpen] = useState(false);
  const [shareCaseDialogOpen, setShareCaseDialogOpen] = useState(false);

  const { organizationId, caseId, chatId, isRestricted, isGuest } =
    useChatDocumentParams();
  const { data: caseInfo } = useGetProject(organizationId, caseId);
  const { project: parentCase, isMaster: isMasterCase } = useParentCase();
  const { data: chatInfo, isLoading } = useGetChat(
    organizationId,
    caseId,
    chatId
  );
  const { data: doclist } = useGetDoclist(organizationId, caseId);
  const { data: shareTokensData } = useGetCaseShareTokens(
    organizationId,
    caseId,
    caseId
  );

  const sharedTokens = shareTokensData?.sharedTokens ?? [];
  const hasMultipleVersions = sharedTokens.length > 1;

  const totalPages = doclist?.tree.reduce((acc, item) => {
    return acc + item.category.pageCount;
  }, 0);

  const { presentUsers } = useCasePresence(caseId);

  return (
    <div className="w-full bg-background border-b border-border justify-between items-center inline-flex h-full">
      <div
        className={`grow shrink basis-0 p-3 justify-between items-center flex w-full`}
      >
        <div className="justify-start items-center gap-3 flex w-full">
          {!isGuest && (
            <div className="h-8 w-8">
              <Button
                variant="secondary"
                size="icon"
                onClick={handleGoBackToCase}
                className="!h-8 !w-8 !p-0"
              >
                <ChevronLeftIcon className="h-[12.8px] w-[12.8px]" />
              </Button>
            </div>
          )}
          <div
            className={`justify-between items-center gap-2 flex ${isRestricted ? (isGuest ? "w-full" : "w-[calc(100%-44px)]") : "w-[calc(100%-32px)]"}`}
          >
            <div
              className={`bg-transparent justify-start items-center gap-2 flex w-full`}
            >
              <div
                className={`items-center gap-2 flex w-full ${isGuest ? (hasMultipleVersions ? "max-w-[calc(100%-250px)]" : "max-w-[calc(100%-133px)]") : presentUsers.length > 0 ? "max-w-[50%]" : "max-w-[calc(100%-90px)]"}`}
              >
                <div
                  className="truncate cursor-pointer w-[fit-content] inline-block text-foreground text-lg font-semibold font-inter leading-7"
                  onClick={() => setCaseInfoPopoverOpen(!caseInfoPopoverOpen)}
                >
                  {isLoading ? (
                    <Skeleton className="h-4 w-[120px]" />
                  ) : (
                    <ChatTitle
                      parentCase={parentCase}
                      isMasterCase={isMasterCase}
                      chatTitle={chatInfo?.title ?? "Untitled Chat"}
                    />
                  )}
                </div>
                {caseInfo && parentCase && chatInfo && (
                  <CaseInfoPopover
                    caseInfo={parentCase}
                    chatInfo={chatInfo}
                    open={caseInfoPopoverOpen}
                    totalPages={totalPages}
                    onOpenChange={setCaseInfoPopoverOpen}
                    isMasterCase={isMasterCase}
                  />
                )}
              </div>

              <div className="flex items-center gap-2 w-full  justify-end">
                {/* {isGuest && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open("/register", "_blank")}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <FormattedMessage
                        id="docList.getFullAccess"
                        defaultMessage="Get Full Access"
                      />
                    </Button>
                  )} */}
                {isRestricted && (
                  <Badge
                    className={`${isGuest ? "w-[81px]" : "w-[81px]"} max-w-[81px] bg-blue-600 text-white rounded-full cursor-default hover:bg-blue-600`}
                  >
                    <FormattedMessage
                      id="docList.viewOnly"
                      defaultMessage="View Only"
                    />
                  </Badge>
                )}
                {isRestricted && isGuest && hasMultipleVersions && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center justify-between gap-2 px-3 py-1.5 w-[calc(100%-100px)] max-w-[153px] border border-border rounded-md bg-background text-sm text-foreground hover:bg-accent focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1">
                        <span className="truncate">
                          {chatInfo?.title || (
                            <FormattedMessage
                              id="docList.untitledVersion"
                              defaultMessage="Untitled Version"
                            />
                          )}
                        </span>
                        <ChevronDownIcon className="h-4 w-4 text-zinc-500 flex-shrink-0" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="start"
                      className="min-w-[220px] max-w-[220px]"
                    >
                      {sharedTokens.map((tokenItem) => (
                        <DropdownMenuItem
                          key={tokenItem.token}
                          onClick={() => navigate(`/share/${tokenItem.token}`)}
                        >
                          {tokenItem.versionName || (
                            <FormattedMessage
                              id="docList.untitledVersion"
                              defaultMessage="Untitled Version"
                            />
                          )}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {!isGuest && <FirstStepsChecklist />}
                {!isGuest && <PresenceAvatars users={presentUsers} />}
              </div>
              {!isRestricted && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShareCaseDialogOpen(true)}
                  className="ml-auto text-blue-600 hover:text-blue-700 flex items-center gap-2"
                >
                  <Share2Icon className="h-4 w-4" />
                  <span className="hidden md:inline">
                    <FormattedMessage
                      id="docList.share"
                      defaultMessage="Share"
                    />
                  </span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      <ShareCaseDialog
        open={shareCaseDialogOpen}
        onOpenChange={setShareCaseDialogOpen}
        organizationId={organizationId}
        caseId={caseId}
        chatId={chatId}
        caseName={caseInfo?.name || ""}
      />
    </div>
  );
};
